import { MsgEditor } from '../MsgEditor/MsgEditor';
import { RECALL_FIELDS } from '../../recall-builder/types';
import type { BuilderToken, FieldKey, RecallField } from '../../recall-builder/types';
import {
  convertCompleteBraces as convertGeneric,
  convertCompleteBracesIn as convertInGeneric,
  mergeText as mergeGeneric,
} from '../../msg/tokens';
import type { MsgMenuEntry } from '../../msg/tokens';

export interface FieldMenuEntry {
  field: RecallField;
  /** Set when the field cannot be used here — shown greyed with this reason. */
  unavailable: string | undefined;
}

export interface FieldEditorProps {
  value: BuilderToken[];
  onChange: (next: BuilderToken[]) => void;
  menu: FieldMenuEntry[];
  placeholder?: string;
  rows?: number;
  ariaLabel: string;
  onMenuOpenChange?: (open: boolean) => void;
  rtl?: boolean;
}

/**
 * The recall builder's message box — now **a thin wrapper** around the shared
 * `MsgEditor` (amendment 25 §9: *build them once, mount them in both places*).
 *
 * The box itself, the atomic chips, the caret-positioned menu and the
 * brace-to-chip conversion all moved to `components/MsgEditor`. What stays here
 * is the recall flavour: which fields exist, and their labels and examples. The
 * reminders editor mounts the same box over appointment fields.
 */
export function FieldEditor({ menu, ...rest }: FieldEditorProps) {
  const entries: MsgMenuEntry<FieldKey>[] = menu.map((m) => ({
    field: {
      key: m.field.key,
      label: m.field.label,
      example: m.field.example,
      mayBeEmpty: m.field.mayBeEmpty,
      ...(m.field.defaultFallback !== undefined ? { defaultFallback: m.field.defaultFallback } : {}),
    },
    unavailable: m.unavailable,
  }));
  return <MsgEditor<FieldKey> menu={entries} {...rest} />;
}

/* ---- Token helpers, recall-flavoured ------------------------------------- *
 * The generic versions take the field-name set as an argument; these bind it to
 * the recall fields, so existing callers (and the spec-23 harness) are unchanged.
 * -------------------------------------------------------------------------- */

const FIELD_KEYS: ReadonlySet<string> = new Set(RECALL_FIELDS.map((f) => f.key));

export function mergeText(nodes: BuilderToken[]): BuilderToken[] {
  return mergeGeneric(nodes);
}

export const normalise = mergeText;

export function convertCompleteBraces(raw: string): BuilderToken[] {
  return convertGeneric<FieldKey>(raw, FIELD_KEYS);
}

export function convertCompleteBracesIn(tokens: BuilderToken[]): BuilderToken[] {
  return convertInGeneric<FieldKey>(tokens, FIELD_KEYS);
}
